/**
 * LandlordTeam — invite, manage, and revoke teammates on the landlord
 * portal. Pairs with landlord_team_service.
 *
 * Roles surfaced (owner is implicit, can't be changed via UI):
 *   • Property manager — full operational access
 *   • Bookkeeper       — payments + CRA + reports
 *   • Leasing agent    — applications + tours + listings
 *   • Read-only        — view-only
 */
import { useEffect, useState, useCallback } from "react";
import { useAuth } from "@/lib/auth";
import { PageHeader, Card, Button, Pill, Loader, EmptyState, Modal, Input, Select } from "@/components/ui/Primitives";
import { Users, Plus, Copy, Trash2, Shield, Crown, Mail, CheckCircle2, Clock } from "lucide-react";
import { toast } from "sonner";

const API = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");

const ROLES = [
  { value: "manager",    label: "Property manager", blurb: "Full operations — listings, tenants, applications, leases, payments, tours, smart locks." },
  { value: "bookkeeper", label: "Bookkeeper",       blurb: "Money side only — payments, CRA filing, reports. Can't change tenants or listings." },
  { value: "leasing",    label: "Leasing agent",    blurb: "Showings + signing — applications, tours, listings. No payment access." },
  { value: "read_only",  label: "Read-only",        blurb: "View-only access to everything. Good for advisors / accountants on retainer." },
];

const ROLE_TONE = { owner: "purple", manager: "green", bookkeeper: "blue", leasing: "amber", read_only: "slate" };

export default function LandlordTeam() {
  const { user } = useAuth();
  const uid = user?.id || user?.user_id || user?.email || "";
  const [team, setTeam] = useState({ items: [], you: null, org: null });
  const [loading, setLoading] = useState(true);
  const [openInvite, setOpenInvite] = useState(false);
  const [openInviteDone, setOpenInviteDone] = useState(null); // {invite_url, role, email}
  const [form, setForm] = useState({ email: "", name: "", role: "manager" });
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (!uid) return;
    setLoading(true);
    try {
      const r = await fetch(`${API}/api/landlord/team`, { headers: { Authorization: `Bearer ${localStorage.getItem("crc_session_token")||""}`, "x-user-id": uid } });
      const d = await r.json();
      setTeam(d);
    } finally { setLoading(false); }
  }, [uid]);
  useEffect(() => { load(); }, [load]);

  const isOwner = team.org?.owner_user_id === uid;

  async function sendInvite(e) {
    e.preventDefault(); setBusy(true);
    try {
      const r = await fetch(`${API}/api/landlord/team/invite`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("crc_session_token")||""}`, "x-user-id": uid },
        body: JSON.stringify(form),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Could not invite");
      setOpenInvite(false);
      setOpenInviteDone({ invite_url: d.invite_url, role: d.role, email: d.email });
      setForm({ email: "", name: "", role: "manager" });
      load();
    } catch (err) { toast.error(String(err.message)); }
    finally { setBusy(false); }
  }

  async function changeRole(member, newRole) {
    setBusy(true);
    try {
      const r = await fetch(`${API}/api/landlord/team/${member.id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("crc_session_token")||""}`, "x-user-id": uid },
        body: JSON.stringify({ role: newRole }),
      });
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        throw new Error(d.detail || "Could not update");
      }
      toast.success(`Role updated to ${ROLES.find(r => r.value === newRole)?.label || newRole}`);
      load();
    } catch (err) { toast.error(String(err.message)); }
    finally { setBusy(false); }
  }

  async function revoke(member) {
    const label = member.display_name || member.email || "this teammate";
    if (!window.confirm(`Remove ${label} from your team?`)) return;
    try {
      const r = await fetch(`${API}/api/landlord/team/${member.id}/revoke`, {
        method: "POST",
        headers: { Authorization: `Bearer ${localStorage.getItem("crc_session_token") || ""}` },
      });
      if (r.ok) { toast.success("Teammate removed"); load(); return; }
      let detail = "";
      try { detail = (await r.json())?.detail || ""; } catch { /* ignore */ }
      if (r.status === 401 || r.status === 403) toast.error("Session expired. Log back in and try again.");
      else if (r.status === 404) { toast.error("Teammate already removed."); load(); }
      else toast.error(detail || `Could not remove (${r.status})`);
    } catch (err) {
      toast.error(err?.message || "Network error — check your connection.");
    }
  }

  async function resendInvite(member) {
    setBusy(true);
    try {
      const r = await fetch(`${API}/api/landlord/team/${member.id}/resend`, {
        method: "POST", headers: { Authorization: `Bearer ${localStorage.getItem("crc_session_token")||""}`, "x-user-id": uid },
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Could not resend");
      if (d.email_status === "sent") toast.success("Invite re-sent");
      else toast.error(`Email send failed — copy the link: ${d.invite_url}`);
      load();
    } catch (err) { toast.error(String(err.message)); }
    finally { setBusy(false); }
  }

  async function copyLink(url) {
    await navigator.clipboard.writeText(url);
    toast.success("Invite link copied");
  }

  if (loading) return <Loader/>;

  return (
    <div>
      <PageHeader
        title="Your team"
        subtitle="Invite the people who help you run your rentals — co-owners, property managers, bookkeepers, leasing agents. Each gets their own login with the right level of access."
        actions={isOwner && <Button onClick={() => setOpenInvite(true)} data-testid="invite-teammate"><Plus className="w-4 h-4"/> Invite teammate</Button>}
      />

      {team.items.length === 0 ? (
        <EmptyState icon={Users} title="Just you so far" description="When you're ready to bring on a property manager, bookkeeper, or leasing agent, click 'Invite teammate' to send them a link with the right permissions."/>
      ) : (
        <div className="space-y-2">
          {team.items.map(m => (
            <Card key={m.id} className="p-4 flex flex-col sm:flex-row sm:items-center gap-3" data-testid={`teammate-${m.id}`}>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-bold text-slate-900">{m.display_name}</span>
                  {m.role === "owner" && <Pill tone="purple"><Crown className="w-2.5 h-2.5 inline mr-1"/> Owner</Pill>}
                  {m.role !== "owner" && <Pill tone={ROLE_TONE[m.role] || "slate"}>{m.role_label || m.role}</Pill>}
                  {m.status === "pending" && <Pill tone="amber"><Clock className="w-2.5 h-2.5 inline mr-1"/> {m.email_status === "failed" ? "Email failed" : "Invite sent"}</Pill>}
                  {m.status === "active" && m.role !== "owner" && <Pill tone="green"><CheckCircle2 className="w-2.5 h-2.5 inline mr-1"/> Active</Pill>}
                </div>
                <div className="text-xs text-slate-500 mt-0.5">{m.email}</div>
                {m.status === "pending" && m.email_status === "failed" && (
                  <div className="text-[11px] text-rose-700 bg-rose-50 border border-rose-200 rounded px-2 py-1 mt-1 inline-block" data-testid={`email-failed-${m.id}`}>Email delivery failed: {m.email_error || "unknown"}</div>
                )}
                {m.status === "pending" && m.email_sent_at && (
                  <div className="text-[11px] text-slate-400 mt-0.5">Email sent {new Date(m.email_sent_at).toLocaleString("en-CA", {dateStyle: "short", timeStyle: "short"})}</div>
                )}
              </div>
              {isOwner && m.role !== "owner" && (
                <div className="flex gap-2 shrink-0 items-center flex-wrap">
                  {m.status === "pending" && (
                    <button onClick={() => resendInvite(m)} disabled={busy} className="text-xs font-bold text-emerald-700 hover:text-emerald-800 inline-flex items-center gap-1" title="Resend invite email" data-testid={`teammate-resend-${m.id}`}><Mail className="w-3 h-3"/> Resend</button>
                  )}
                  <select value={m.role} onChange={(e) => changeRole(m, e.target.value)} disabled={busy} className="px-2 py-1.5 text-xs border border-slate-300 rounded outline-none focus:border-emerald-500" data-testid={`teammate-role-${m.id}`}>
                    {ROLES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                  </select>
                  <button onClick={() => revoke(m)} className="text-rose-600 hover:bg-rose-50 p-1.5 rounded" title="Remove" data-testid={`teammate-revoke-${m.id}`}><Trash2 className="w-3.5 h-3.5"/></button>
                </div>
              )}
            </Card>
          ))}
        </div>
      )}

      {!isOwner && team.you && (
        <Card className="p-4 mt-4 bg-blue-50 border-blue-200">
          <div className="text-xs text-blue-900">You&apos;re a <b>{team.you.role_label || team.you.role}</b> on this team. To invite more people or change permissions, ask the owner.</div>
        </Card>
      )}

      <Modal open={openInvite} onClose={() => setOpenInvite(false)} title="Invite a teammate" testid="invite-modal">
        <form onSubmit={sendInvite} className="space-y-4">
          <Input label="Email" type="email" required value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} testid="invite-email"/>
          <Input label="Name (optional)" value={form.name} onChange={(e) => setForm({...form, name: e.target.value})}/>
          <div>
            <label className="text-[10px] uppercase tracking-widest font-bold text-slate-500 block mb-1">Role</label>
            <div className="space-y-2">
              {ROLES.map(r => (
                <label key={r.value} className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer ${form.role === r.value ? "border-emerald-500 bg-emerald-50" : "border-slate-200 hover:border-slate-400"}`} data-testid={`role-option-${r.value}`}>
                  <input type="radio" name="role" value={r.value} checked={form.role === r.value} onChange={() => setForm({...form, role: r.value})} className="mt-0.5"/>
                  <div className="min-w-0">
                    <div className="font-bold text-sm text-slate-900">{r.label}</div>
                    <div className="text-xs text-slate-600">{r.blurb}</div>
                  </div>
                </label>
              ))}
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="ghost" onClick={() => setOpenInvite(false)}>Cancel</Button>
            <Button type="submit" disabled={busy} data-testid="invite-submit">{busy ? "Sending…" : "Send invite"}</Button>
          </div>
        </form>
      </Modal>

      <Modal open={!!openInviteDone} onClose={() => setOpenInviteDone(null)} title="Invite sent" testid="invite-done-modal">
        {openInviteDone && (
          <div className="space-y-4">
            <div className="text-center py-2">
              <Mail className="w-12 h-12 text-emerald-600 mx-auto mb-2"/>
              <div className="font-display text-lg font-bold text-slate-900">We emailed your invite to</div>
              <div className="text-sm text-emerald-700 font-bold">{openInviteDone.email}</div>
              <div className="text-xs text-slate-500 mt-1">They&apos;ll click the link to set up their access. Email not arriving? Copy the link below and send it directly.</div>
            </div>
            <div className="rounded-lg bg-slate-50 border border-slate-200 p-3 flex items-center gap-2">
              <code className="text-xs text-slate-700 truncate flex-1" data-testid="invite-url">{openInviteDone.invite_url}</code>
              <button onClick={() => copyLink(openInviteDone.invite_url)} className="text-xs font-bold text-emerald-700 hover:text-emerald-800 inline-flex items-center gap-1 shrink-0" data-testid="copy-invite-url"><Copy className="w-3.5 h-3.5"/> Copy</button>
            </div>
            <div className="flex justify-end pt-1">
              <Button onClick={() => setOpenInviteDone(null)}>Done</Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
